import { OwlDateTimeModule, OwlNativeDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import { CommonModule } from '@angular/common';
import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';

import { DatetimeCustomEvent, IonDatetime, IonModal, IonPopover } from '@ionic/angular/standalone';

import { DateTime } from 'luxon';

import { getSoonestTimeByInterval, roundTimeToNearestTimeInterval } from '../../../helpers';
import { GlobalsClientInterface } from '../../../types';
import { InputButtonComponent } from '../../buttons';
import { InputWrapperComponent } from '../input-wrapper/input-wrapper.component';

@Component({
    selector: 'lib-datetime-input',
    standalone: true,
    imports: [
        IonPopover,
        CommonModule,
        IonDatetime,
        IonModal,
        InputButtonComponent,
        OwlDateTimeModule,
        OwlNativeDateTimeModule,
        InputWrapperComponent
    ],
    templateUrl: './datetime-input.component.html',
    styleUrl: './datetime-input.component.css',
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class DatetimeInputComponent implements OnInit, OnChanges {
    /* eslint-disable */
    selectedValue: string | null | undefined = '';
    dt: DateTime = DateTime.local();
    pickerMinDate: string | undefined = '';
    pickerMaxDate: string | undefined = '';
    pickerStartAtTime: string | null | undefined = undefined;
    minBookableDate: string | undefined = '';
    lastHourOfTheDay = 21;
    initChanged = false;
    minStartTime: string | null | undefined = undefined;
    maxStartTime: string | null | undefined = undefined;
    initialMinStartTime: string | null | undefined = undefined;
    @ViewChild('dateModal') dateModal: any;
    @ViewChild('datePicker') datePicker: any;
    @Input() initialValue: string | null | undefined = undefined;
    @Input() label = 'Date';
    @Input() id = 'selectDate';
    @Input() datePickerId = 'datePicker';
    @Input() pickerFormat = 'MMMM D YYYY';
    @Input() displayFormat = 'DDDD, MMMM D, YYYY';
    @Input() dateTextFormat = 'EEEE, MMMM d, YYYY';
    @Input() placeholder: string | undefined = undefined;
    @Input() disabled: boolean = false;
    @Input() dayValues: string[] | null | undefined = undefined;
    @Input() presentation: 'date' | 'time' | 'month-year' = 'date';
    @Input() minDate: string | undefined = undefined; //min date
    @Input() maxDate: string | undefined = undefined; //max date
    @Input() minTime: any | undefined = undefined;
    @Input() maxTime: any | undefined = undefined;
    @Input() useDefaultBookingMinMaxDates = true;
    @Input() fControlName: string | undefined = undefined; //formControlName
    @Input() value: string | undefined = undefined;
    @Input() required = false;
    @Input() ionItemWrap = true; //wrap in ion-item wrapper
    @Input() isInvalidBorderDisplay = false;
    @Input() setDefaultValue: boolean = false;
    @Input() preferWheel: boolean = false;
    @Input() modalType: 'modal' | 'popover' = 'modal';
    @Input() removeAddedSpacing: boolean = false;
    @Input() restrictSelectableHours: boolean = true;
    @Output() selectedValueEmitter = new EventEmitter<string | null | undefined>();
    /* eslint-disable */
    private globals: GlobalsClientInterface;

    constructor(@Inject('globals') globals: GlobalsClientInterface) {
        this.globals = globals;
    }

    ngOnInit(): void {
        this.init();
    }

    ngOnChanges(): void {
        if (this.setDefaultValue) {
            this.selectedValue =
                this.initialValue && this.initialValue !== 'Invalid DateTime' ? this.initialValue : null;
        }

        if (this.selectedValue && this.setDefaultValue) {
            this.pickerStartAtTime = this.selectedValue;
        }

        setTimeout(() => {
            this.datePicker?.confirm();
        }, 0);
    }

    init() {
        // console.log('Datetime-Input init ' + this.id);
        const minTime = this.getMinTime();
        this.pickerMinDate = this.setMinDate();
        this.pickerMaxDate = this.setMaxDate();

        this.minStartTime = minTime;
        this.pickerStartAtTime = minTime;
        // console.log(`minTime : ${minTime}`);
        this.maxStartTime = this.getMaxTime();

        // console.log('min start: ' + this.minStartTime);
        // console.log('max start: ' + this.maxStartTime);

        if (this.initChanged) return;

        this.selectedValue = this.initialValue;
        // console.log('selectedValue init ' + this.selectedValue?.toString());

        if (this.setDefaultValue) {
            this.initDefaultValue();
        }

        this.selectedValueEmitter.emit(this.selectedValue);
    }

    initDefaultValue() {
        this.selectedValue = this.initialMinStartTime;
    }

    setMinStartTime(datetime?: string) {
        const currentDT = DateTime.local();
        const date = datetime || currentDT.toISO();
        const dateDT = DateTime.fromISO(date).set({ second: 0 });
        const minStartDate = DateTime.fromISO(this.pickerMinDate || '');
        const isEarliestDay = dateDT.hasSame(minStartDate, 'day');

        if (isEarliestDay) {
            this.minStartTime = getSoonestTimeByInterval(30);
            this.setStartAtTime(this.minStartTime);
        } else {
            const date =
                dateDT.set({ hour: 0, minute: 0, second: 0 }).toString() ||
                currentDT.set({ hour: 0, minute: 0, second: 0 }).plus({ days: 1 }).toString();

            this.minStartTime = date.toString();

            const roundedTime = roundTimeToNearestTimeInterval(date, 30) || '';

            const pickerTimeStartAt = DateTime.fromISO(roundedTime).set({ hour: currentDT.hour }).toISO();

            this.setStartAtTime(pickerTimeStartAt);
        }
    }

    getMinTime(datetime?: string) {
        const updatedTime = datetime ? roundTimeToNearestTimeInterval(datetime, 30) : getSoonestTimeByInterval(30);

        console.log(updatedTime);
        this.initialMinStartTime = updatedTime;

        return updatedTime;
    }

    getMaxTime() {
        const dt = DateTime.local();
        const newDate = dt
            .plus({ hours: this.globals.bookingSettings.minimumBookableHours })
            .set({ hour: this.globals.bookingSettings.lastHourOfDayForBooking, minute: 59, second: 0 })
            .toString();
        return newDate;
    }

    setMaxDate() {
        if (this.maxDate || this.useDefaultBookingMinMaxDates === false) return this.maxDate;

        const dt = DateTime.local();
        const newDate = dt.plus({ months: this.globals.bookingSettings.maxBookableMonths || 0 });

        return newDate.toFormat('yyyy-LL-dd');
    }

    setMinDate() {
        if (this.minDate || this.useDefaultBookingMinMaxDates === false) return this.minDate;

        let newDate = DateTime.fromISO(this.initialMinStartTime || '');

        return newDate.toFormat('yyyy-LL-dd');
    }

    onDatetimeChange(event: DatetimeCustomEvent) {
        // console.log(event.detail.value);
        const date = event.detail.value?.toString();
        const value = date ? DateTime.fromISO(date).toString() : undefined;

        // console.log(value);
        this.selectedValue = value;

        const emittedDate = date ? DateTime.fromISO(date).toFormat('yyyy-MM-dd') : undefined;
        // console.log(emittedDate);
        this.selectedValueEmitter.emit(emittedDate);
    }

    onTimeChange(event: any) {
        const value = DateTime.fromJSDate(event.value).toString();

        this.selectedValue = value;
        this.pickerStartAtTime = value;

        const emittedTime = DateTime.fromJSDate(event.value).toFormat('HH:mm:00');
        console.log(emittedTime);

        this.selectedValueEmitter.emit(emittedTime);
    }

    setInputValue(value: string | null | undefined) {
        console.log('SET INPUT VALUE ' + value);
        this.selectedValue = value;
        console.log(this.selectedValue);
        // this.datePicker?.confirm();
    }

    setStartAtTime(value: string | null | undefined) {
        this.pickerStartAtTime = value || undefined;
        console.log(this.pickerStartAtTime);
    }

    reset() {
        this.selectedValue = undefined;
        this.selectedValueEmitter.emit(undefined);
    }
}
